import React, { useMemo } from 'react';
import { Loader2, LucideIcon, Network } from 'lucide-react';
import { useAccountL2VPNCircuits } from '@/domains/l2vpns/services/account-l2vpn-circuits';
import { L2VPNCircuitRecord } from '@/shared/types/l2vpnTypes';
import AccountL2VPNCircuitCard from '@/domains/l2vpns/components/AccountL2VPNCircuitCard';
import EmptyView from '@/shared/components/EmptyView';
import SearchBar from '@/shared/components/SearchBar';

export const L2VPNCircuitsTab = () => {

  // Fetch L2VPN circuits data
  const { data: l2vpnCircuits, isLoading: isLoadingL2VPNCircuits } = useAccountL2VPNCircuits(true);

  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredRecords = useMemo(() => {
    return l2vpnCircuits?.flatMap(circuit =>
      circuit.records || []
    ).filter(record =>
      record.circuitName?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
  }, [l2vpnCircuits, searchTerm]);

  const renderResourceGrid = (
    items: L2VPNCircuitRecord[],
    renderItem: (item: L2VPNCircuitRecord) => React.ReactNode,
    EmptyIcon: LucideIcon,
    emptyTitle: string,
    emptyDescription: string
  ) => {
    if (items.length === 0) {
      return (
        <EmptyView
          icon={EmptyIcon}
          title={emptyTitle}
          description={emptyDescription}
        />
      );
    }

    return (
      <div className="space-y-4">
        {items.map(renderItem)}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
        <div>
          <h1 className="text-3xl font-bold">L2VPN Circuits</h1>
          <p className="text-lg">View L2VPN circuits and their assignments to terminals in this account</p>
        </div>
      </div>

      {l2vpnCircuits && l2vpnCircuits.length > 0 && (
        <>
          {/* Search Section */}
          <div className="relative">
            <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search circuits by ID, POP name, or circuit name..." />
          </div>
        </>
      )}

      {/* Content */}
      {isLoadingL2VPNCircuits ? (
        <div className="flex justify-center items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading L2VPN circuits...</p>
        </div>
      ) : (
        renderResourceGrid(
          filteredRecords || [],
          (record) => (
            <AccountL2VPNCircuitCard
              key={record.id}
              record={record}
            />
          ),
          Network,
          searchTerm ? "No Matching L2VPN Circuits" : "No L2VPN Circuits",
          searchTerm ? "Try adjusting your search criteria." : "No L2VPN circuits are currently assigned to this account."
        )
      )}
    </div>
  );
}

